import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppAuth } from '../../hooks/useAppAuth';
import { useAuth } from './AuthProvider';
import { useProfile } from '../../hooks/useProfile';
import { Loader2 } from 'lucide-react';
import { LoadingTroubleshootPanel } from './LoadingTroubleshootPanel';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

// DEV-only redirect logging helper
const logRedirect = (from: string, to: string, reason: string) => {
    if (import.meta.env.DEV) {
        console.log(`[Redirect] ${from} → ${to} (${reason})`);
    }
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    // ALL hooks must be called unconditionally at the top level
    const { authStatus, authLoading, profileTimeoutError } = useAppAuth();
    const { authReady, userId, session } = useAuth();
    const location = useLocation();
    const pathname: string = location.pathname; // Explicit string type to prevent literal narrowing
    const navigate = useNavigate();
    const redirectDoneRef = useRef(false);
    const lastRedirectRef = useRef<string | null>(null);
    
    // Get profile loading state for troubleshooting panel
    const { loading: profileLoading } = useProfile(authReady && userId ? session : null);

    // Use useEffect to redirect - NEVER use Navigate in render (causes hard reloads)
    // All redirect logic must be inside useEffect, not before hooks
    useEffect(() => {
        if (!authReady || authLoading) return;

        // Guard: Only redirect once per decision change
        if (redirectDoneRef.current) return;

        // Only check auth status after authReady is true
        if (authStatus === 'logged_out') {
            // CRITICAL: Don't redirect to /login if already on /login
            if (pathname === '/login') return;
            
            if (lastRedirectRef.current !== '/login') {
                redirectDoneRef.current = true;
                lastRedirectRef.current = '/login';
                logRedirect(pathname, '/login', 'not authenticated');
                navigate('/login', { replace: true, state: { from: location } });
            }
            return;
        }

        // Onboarding is disabled - all authenticated users can access protected routes

        // Reset redirect done flag if auth status changed (e.g., from logged_out to logged_in)
        if (authStatus === 'logged_in' && lastRedirectRef.current) {
            redirectDoneRef.current = false;
            lastRedirectRef.current = null;
        }
    }, [authReady, authLoading, authStatus, pathname, navigate, location]);

    // Early returns MUST come AFTER all hooks are declared
    // NEVER redirect unless authReady === true
    if (!authReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader2 className="size-8 animate-spin text-slate-300" />
                <span className="ml-3 text-slate-500">Loading...</span>
            </div>
        );
    }

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader2 className="size-8 animate-spin text-slate-300" />
            </div>
        );
    }

    return <>{children}</>;
};

export const ProfileGate: React.FC<ProtectedRouteProps> = ({ children }) => {
    // ALL hooks must be called unconditionally at the top level
    const { authStatus, authLoading, profileTimeoutError } = useAppAuth();
    const { authReady, userId, session } = useAuth();
    const location = useLocation();
    const pathname: string = location.pathname; // Explicit string type to prevent literal narrowing
    const navigate = useNavigate();
    const redirectDoneRef = useRef(false);
    const lastRedirectRef = useRef<string | null>(null);
    
    // Get profile loading state for troubleshooting panel
    const { loading: profileLoading } = useProfile(authReady && userId ? session : null);

    // Use useEffect to redirect - NEVER use Navigate in render (causes hard reloads)
    // All redirect logic must be inside useEffect, not before hooks
    useEffect(() => {
        if (!authReady || authLoading) return;

        // Onboarding is disabled - ProfileGate is no longer needed
        // All authenticated users can access protected routes
        // Only redirect if logged out
        if (authStatus === 'logged_out') {
            if (pathname === '/login') return; // Already on login
            if (lastRedirectRef.current !== '/login') {
                redirectDoneRef.current = true;
                lastRedirectRef.current = '/login';
                logRedirect(pathname, '/login', 'not authenticated');
                navigate('/login', { replace: true });
            }
            return;
        }
    }, [authReady, authLoading, authStatus, pathname, navigate]);

    // Early returns MUST come AFTER all hooks are declared
    // NEVER redirect unless authReady === true
    if (!authReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-4">
                <div className="text-center max-w-md w-full">
                    <Loader2 className="size-8 animate-spin text-slate-300 mx-auto mb-3" />
                    <span className="text-slate-500 block mb-4">Loading...</span>
                    <LoadingTroubleshootPanel timeoutError={profileTimeoutError || null} profileLoading={profileLoading} />
                </div>
            </div>
        );
    }

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-4">
                <div className="text-center max-w-md w-full">
                    <Loader2 className="size-8 animate-spin text-slate-300 mx-auto mb-3" />
                    <span className="text-slate-500 block mb-4">Loading...</span>
                    <LoadingTroubleshootPanel timeoutError={profileTimeoutError || null} profileLoading={profileLoading} />
                </div>
            </div>
        );
    }

    return <>{children}</>;
};
